/**
 * ContentFeedHelper
 *
 * @author Copyright (c) 2021 Salesforce.com.
 * @ownership Appexchange Everywhere
 */
import { v4 as uuidv4 } from 'uuid';
import { CachedListing, CategoryData } from '~/modules/appx_site_schema';
import { APPX_SITE_URL } from '~/modules/config';
import { buildListingCategory, flattenBusinessNeeds } from '~/modules/helpers/categoryHelper';
import { getListingLogo } from '~/modules/helpers/listingHelper';
import { generateBaseUrl } from '~/modules/helpers/routingHelper';
import { Routes } from '~/modules/routes';
import { ListingResponse } from '~/modules/services/listing_service';
import { siteConfigStore } from '~/modules/stores/siteConfigStore';
import { handleException } from '~/modules/utilities/error_utilities';
import { isNotNull } from '~/modules/utilities/object_utils';
import { Category, Content, ListingCategory } from '~/schemas/content-schema-v1';

export function parseCategory(category: CategoryData): Category {
    return {
        id: String(category.id),
        callToAction: {
            name: category.name,
            url: category.url,
            label: category.name,
        },
    };
}

export function cleanCategories(categories: ListingCategory[], type?: string): ListingCategory[] {
    const baseUrl = type ? `${generateBaseUrl()}/appxstore?type=${type}&` : `${generateBaseUrl()}/appxstore?`;
    if (categories && categories.length) {
        // @ts-expect-error * Automated Type Error Fix
        return categories
            ?.map((category) => {
                if (!category) {
                    return null;
                }
                // There are null values comming
                // @ts-expect-error * Automated Type Error Fix
                const name = category.name?.split('>').pop().trim();
                if (!name) {
                    return undefined;
                }
                return {
                    filter: `${baseUrl}filter=${category.filter}`,
                    name,
                };
            })
            .filter(Boolean);
    }
    return [];
}

// This function is populating a lot of listing related fields, but we only pass customerStory data into it.
// TODO: Refactor this to only return fields relevant to customerStory items.
// @ts-expect-error * Automated Type Error Fix
export function parseCustomerStory(customerStory, addListingUrl = false): Content {
    const categories = cleanCategories(
        customerStory.categories || customerStory.listingCategories,
        customerStory.wrapper_type,
    );
    const logo = customerStory.logos?.length > 0 ? customerStory.logos[0]?.mediaId : customerStory.logoUrl;
    const base = siteConfigStore.getState().isAgentExchange ? APPX_SITE_URL : '';
    const listingUrl = addListingUrl
        ? `${base}/${Routes.LISTING}?listingId=${customerStory.external_id || customerStory.id}`
        : '';
    return {
        key: uuidv4(),
        id: customerStory.id,
        title: customerStory.name || customerStory.title,
        type: customerStory.wrapper_type || '',
        description: customerStory.tagline || customerStory.description,
        publisher: customerStory.publisherName,
        listingUrl,
        averageRating: customerStory.rating || customerStory.averageRating,
        reviewsAmount: customerStory.numberOfUserReviews || customerStory.reviewsAmount,
        callToAction: {
            url: customerStory.learn_more,
            label: customerStory.action_label,
        },
        image: logo || customerStory.image,
        listingCategories: categories || [],
        alt_text: customerStory.alt_text || '',
    };
}

export function buildHomeCategorySection(categories: Array<CategoryData>) {
    const sortByOrder = (a: CategoryData, b: CategoryData) => a.order - b.order;
    return categories.sort(sortByOrder).map(parseCategory);
}

export function buildHomeListingSection(listings: Array<CachedListing>): Content[] {
    const results = listings
        .map((cachedListing) => {
            try {
                const listing = cachedListing.listing as ListingResponse;
                return {
                    id: listing.id,
                    title: listing.name,
                    type: listing.type || listing?.technology,
                    description: listing.description,
                    image: getListingLogo(listing),
                    listingUrl: buildListingUrl(cachedListing),
                    publisher: listing.publisher.name,
                    averageRating: listing.reviewsSummary?.averageRating,
                    reviewsAmount: listing.reviewsSummary?.reviewCount,
                    listingCategories: buildCategories(listing),
                    tzId: listing.external_id,
                    appExchangeId: listing.id,
                    oafId: listing.oafId,
                    external_id: listing.oafId,
                    sponsoredCampaigns: cachedListing.sponsoredCampaigns,
                };
            } catch (error) {
                handleException(error, 'contentFeedHelper.buildHomeListingSection');
                return null;
            }
        })
        .filter(isNotNull); // filter out failed results

    return results as Content[];
}

function buildListingUrl(cmsListing: CachedListing): string {
    const base = siteConfigStore.getState().isAgentExchange ? APPX_SITE_URL : '';
    const id = cmsListing.external_id || cmsListing.id;
    let listingUrl = `${base}/${Routes.LISTING}?listingId=${id}`;
    if (cmsListing.sponsoredCampaigns?.length > 0) {
        const { placement_id } = cmsListing.sponsoredCampaigns[0];
        listingUrl += `&channel=featured&placement=${placement_id}`;
    }
    return listingUrl;
}

function buildCategories(listing: ListingResponse) {
    return flattenBusinessNeeds(listing.businessNeeds).map(buildListingCategory);
}
